import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";

import { useMe, usePricingOffer, useRegisterPlanIntent } from "../api/queries";
import { Icon } from "../components/Icons";

const TEAM_VALUE = [
  "Automatically turn merged GitHub and GitLab work into continuous evaluations",
  "Get the best setup for each repository, task type, and complexity",
  "Compare full trajectories and catch quality, cost, and speed regressions",
  "Bring the whole team into one SSO workspace with no per-seat fees",
];

const TEAM_SIZES = ["1–19", "20–50", "51–100", "101+"];
const START_TIMES = ["Now", "This month", "This quarter", "Just exploring"];

/**
 * What to quote when the server cannot tell us which arm this user is in.
 *
 * The price is an experiment, and an experiment must not be able to break the
 * page it is running on. A blank price with a dead Upgrade button loses the
 * click outright — the one thing this page exists to capture — to protect an
 * analytics row, which is the trade the wrong way round.
 *
 * So the page falls back to the control price and the click is recorded
 * UNATTRIBUTED: the server only credits an arm it has recorded as shown, and
 * it never saw this render. A session that falls back is missing from every
 * arm's numerator and denominator alike, which is what it should be — it is
 * not evidence about any price.
 */
const CONTROL_PRICE = 499;

/**
 * The price-qualified Team offer.
 *
 * The first click is useful because the price is already visible. The second
 * step is stronger: it records who completed an activation request, together
 * with whether the workspace fits the current ICP and when they want to buy.
 */
export function Plans() {
  const { data: me } = useMe();
  const slug = me?.workspaces[0]?.slug;
  const offer = usePricingOffer(slug);
  // `isError` here means react-query has already exhausted its retries.
  const price = offer.data?.price_monthly ?? (offer.isError ? CONTROL_PRICE : undefined);
  const clickIntent = useRegisterPlanIntent(slug);
  const activationIntent = useRegisterPlanIntent(slug);
  const [activationOpen, setActivationOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [teamSize, setTeamSize] = useState("20–50");
  const [startTime, setStartTime] = useState("Now");

  const openActivation = () => {
    // Opening the next step is part of the user's action, not a reward for
    // successful analytics, so this deliberately does not await the request.
    clickIntent.mutate({ plan: "pro", source: "plans_page" });
    setActivationOpen(true);
  };

  const requestActivation = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    activationIntent.mutate(
      {
        plan: "pro",
        source: "plans_activation",
        team_size: teamSize,
        purchase_timeline: startTime,
      },
      { onSuccess: () => setSubmitted(true) },
    );
  };

  return (
    <div className="upgrade-page">
      <div className="upgrade-back-row">
        <Link to="/">← Back to results</Link>
      </div>

      <section className="upgrade-stage">
        <div className="upgrade-pitch">
          <span className="upgrade-kicker"><i /> BERBench Team</span>
          <h1>
            Get better results from the coding agent <em>your team already uses.</em>
          </h1>
          <p>
            BERBench tests models, instructions, tools, and workflows against
            real work from your repositories—so you can improve performance,
            understand behavior changes, and catch regressions before rollout.
          </p>
          <div className="upgrade-proof">
            <span>See what changed—and why</span>
            <strong>
              Compare complete agent trajectories between settings, not just
              their final scores.
            </strong>
          </div>
        </div>

        <div className="upgrade-offer">
          <div className="offer-top">
            <span>Team</span>
            <span>per workspace</span>
          </div>
          <div className="offer-price">
            <strong>{price === undefined ? "—" : `$${price}`}</strong>
            <span>/ month</span>
          </div>
          <p>For engineering organizations with 20–50 developers:</p>
          <ul>
            {TEAM_VALUE.map((value) => (
              <li key={value}><Icon name="check" /> {value}</li>
            ))}
          </ul>
          <div className="offer-meta">
            <span>Up to 50 engineers</span>
            <span>Model usage paid separately</span>
          </div>
          <button
            type="button"
            className="offer-button"
            onClick={openActivation}
            disabled={price === undefined}
          >
            Upgrade to Team <Icon name="arrow" />
          </button>
          <small>SSO and centralized access controls included.</small>
        </div>
      </section>

      <div className="upgrade-trust">
        <span><Icon name="check" /> Source code and execution stay in your environment</span>
        <span><Icon name="check" /> Cost controls before automated runs</span>
        <span><Icon name="check" /> Reuse the same evaluations as tools change</span>
      </div>

      {activationOpen && (
        <ActivationDialog
          submitted={submitted}
          pending={activationIntent.isPending}
          error={activationIntent.isError}
          teamSize={teamSize}
          startTime={startTime}
          price={price ?? CONTROL_PRICE}
          onTeamSize={setTeamSize}
          onStartTime={setStartTime}
          onClose={() => setActivationOpen(false)}
          onSubmit={requestActivation}
        />
      )}
    </div>
  );
}

function ActivationDialog({
  submitted, pending, error, teamSize, startTime, price,
  onTeamSize, onStartTime, onClose, onSubmit,
}: {
  submitted: boolean;
  pending: boolean;
  error: boolean;
  teamSize: string;
  startTime: string;
  price: number;
  onTeamSize: (value: string) => void;
  onStartTime: (value: string) => void;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <div className="activation-backdrop" onMouseDown={onClose}>
      <section
        className="activation-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="activation-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className="activation-close" type="button" onClick={onClose} aria-label="Close">×</button>
        {submitted ? (
          <div className="activation-success">
            <span><Icon name="check" /></span>
            <h2 id="activation-title">Your Team request is in.</h2>
            <p>We’ll be in touch with activation details.</p>
            <button type="button" className="button primary" onClick={onClose}>Back to BERBench</button>
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <span className="activation-kicker">Complete your upgrade</span>
            <h2 id="activation-title">Activate BERBench Team</h2>
            <p>
              We’re activating Team workspaces in batches. Tell us when you
              want to start and we’ll send you the next steps.
            </p>
            <label>
              <span>Engineering team size</span>
              <select value={teamSize} onChange={(event) => onTeamSize(event.target.value)}>
                {TEAM_SIZES.map((size) => <option key={size}>{size}</option>)}
              </select>
            </label>
            <label>
              <span>When do you want to start?</span>
              <select value={startTime} onChange={(event) => onStartTime(event.target.value)}>
                {START_TIMES.map((time) => <option key={time}>{time}</option>)}
              </select>
            </label>
            <div className="activation-summary">
              <span>BERBench Team</span>
              <strong>${price} / month</strong>
              <small>Up to 50 engineers · model usage separate</small>
            </div>
            {error && <p className="activation-error">We couldn’t save the request. Please try again.</p>}
            <button type="submit" className="offer-button" disabled={pending}>
              {pending ? "Requesting…" : "Request activation"} <Icon name="arrow" />
            </button>
            <small className="activation-note">This request does not charge you.</small>
          </form>
        )}
      </section>
    </div>
  );
}
